import type { BookmarkDto, BookmarkFolder } from '@mimir/common/dto/bookmarkDto';
import type { TenantConfigDto } from '@mimir/common/dto/tenantModel';
import { logErrorAndShowToast } from '../util/uiError';
import { getBookmarks, getBookmarksV2, readBookmarkFolders } from '../util/api';
import { reactive } from '../compat/vueCompat';
import { getGlobalStore } from '../store';

interface BookmarksState {
  systemBookmarks: BookmarkDto[];
  loadingSystemBookmarks: boolean;
  systemBookmarksFolders: BookmarkFolder[];
  systemBookmarksFolderEtag: string | undefined;
}

export const bookmarksState: BookmarksState = reactive<BookmarksState>({
  systemBookmarks: [],
  systemBookmarksFolders: [],
  systemBookmarksFolderEtag: undefined,
  loadingSystemBookmarks: false,
});

export interface LoadSystemBookmarksOptions {
  useCache: boolean;
  loadFolders: boolean;
}

export interface SystemBookmarksResult {
  bookmarks: BookmarkDto[];
  bookmarkFolders: BookmarkFolder[];
  folderEtag: string | undefined;
}

/**
 * Loads system bookmarks from the API and caches them.
 * @param options.useCache - If true, returns cached version if available. If false, always fetches fresh data.
 * @param options.loadFolders - If true, also loads bookmark folders (only for V2).
 * @returns Promise containing bookmarks and folders data
 */
export async function loadSystemBookmarks(
  options: LoadSystemBookmarksOptions
): Promise<SystemBookmarksResult> {
  const { useCache, loadFolders } = options;

  if (useCache && bookmarksState.systemBookmarks.length > 0) {
    return {
      bookmarks: bookmarksState.systemBookmarks,
      bookmarkFolders: bookmarksState.systemBookmarksFolders,
      folderEtag: bookmarksState.systemBookmarksFolderEtag,
    };
  }

  bookmarksState.loadingSystemBookmarks = true;
  const isV2Enabled = isBookmarkV2FlagEnabled();

  try {
    const bookmarksPromise = isV2Enabled ? getBookmarksV2() : getBookmarks();
    const foldersPromise = (isV2Enabled && loadFolders) ? readBookmarkFolders() : null;

    const [bookmarks, folderResult] = await Promise.all([
      bookmarksPromise,
      foldersPromise,
    ]);

    bookmarksState.systemBookmarks = bookmarks;

    if (folderResult != null) {
      bookmarksState.systemBookmarksFolders = folderResult.folders ?? [];
      bookmarksState.systemBookmarksFolderEtag = folderResult.eTag;
    } else {
      bookmarksState.systemBookmarksFolders = [];
      bookmarksState.systemBookmarksFolderEtag = undefined;
    }

    return {
      bookmarks,
      bookmarkFolders: bookmarksState.systemBookmarksFolders,
      folderEtag: bookmarksState.systemBookmarksFolderEtag,
    };
  } catch (error) {
    logErrorAndShowToast('Failed to load system bookmarks', error, 'ex in loadSystemBookmarks');
  } finally {
    bookmarksState.loadingSystemBookmarks = false;
  }

  return { bookmarks: [], bookmarkFolders: [], folderEtag: undefined };
}

export function isBookmarkV2FlagEnabled(): boolean {
  const store = getGlobalStore();
  return (store.getters.featureFlags as TenantConfigDto['featureFlags'])?.bookmarkV2 ?? false;
}

/**
 * Gets a system bookmark by ID from the cache
 */
export function getSystemBookmarkById(id: string): BookmarkDto | undefined {
  return bookmarksState.systemBookmarks.find(bookmark => bookmark.id === id);
}
